/* eslint-disable no-param-reassign */
import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Grid } from '@mui/system';
import { isEmpty, partition } from 'lodash';
import { GPC, MeasurementDateName, ValuationApproachGPC } from 'api';
import { SYSTEM_COLLAPSE, USER_EXPAND } from 'common/actions/row-groups/types';
import { VALUATIONS_PAGE_KEY, VALUATIONS_PAGE_VALUE } from 'common/constants/notes';
import { VALUATIONS_SLUG } from 'common/constants/valuations';
import { useFormat } from 'common/hooks';
import { rowsGroupsReducer } from 'common/reducers/rowsGroups';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport';
import { CustomCompactContainer, CustomContainer } from 'components';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import CompGroupContext, { CompGroupContextType } from 'context/CompGroupContext';
import { useLayoutContext } from 'context/LayoutContext';
import AddGpcDialog from 'pages/Valuations/approaches/guidelinePublicCompanies/AddGpcDialog';
import ExchangeRatesReferenceTable from 'pages/Valuations/approaches/guidelinePublicCompanies/ExchangeRatesReferenceTable/ExchangeRatesReferenceTable';
import { parseGpcComparison } from 'pages/Valuations/approaches/guidelinePublicCompanies/gpc/config/utilities';
import { MARKET_CURRENCY, PdfExportTargets, PUBLIC_COMPANIES, SORT_ASC, SORT_DESC } from 'pages/Valuations/util/constants';
import { useCellOptions } from 'pages/ValuationsAllocation/hooks';
import { CapIQCompanyData, Note, PublicComp } from 'pages/ValuationsAllocation/types';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { useNotes } from 'services/hooks/notes';
import { getNumberValue, getObjectValue, getStringValue } from 'utilities';
import { FIRST_NON_FORWARD_ID, SELECTION, VALUATION_GPC_TABLE_SLUG } from './constants';
import getGPCRowConfig from './gpc/config/getRowConfig';
import gpcRowTransformer from './gpc/config/rowTransformer';
import {
  GPCExtended,
  GPCTableData,
  GuidelinePublicCompaniesProps,
  ValuationApproachGpcCompGroupExtended,
  ValuationApproachGPCExtended,
} from './GuidelinePublicCompanies.props';
import { PERFORMANCE_METRICS_SPREADSHEET_SLUG } from './PerformanceMetrics/common/constants/performanceMetrics';
import {
  createColumns as performanceMetricsCreateColumns,
  rowConfig as performanceMetricsRowConfig,
  useCustomClasses as usePerformanceMetricsCustomClasses,
} from './PerformanceMetrics/config';
import { ReverseParsedValuationApproachGPC } from './types';
import getFlattenedData from './utils/getFlattenedData';
import { GPCAttributes } from './utils/types';
import { useCombinedGPCComparisonPerfMetricAttrs } from './utils/useCombinedGPCComparisonPerfMetricAttrs';
import WidgetDrawer from '../../../../components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from '../../../../components/Drawer/widget-drawer/WidgetDrawer.props';

const VALUATION_GPC_PUBLIC_COMPANIES_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${VALUATION_GPC_TABLE_SLUG}-container`;
const VALUATION_GPC_PERFORMANCE_METRICS_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${PERFORMANCE_METRICS_SPREADSHEET_SLUG}-container`;
const VALUATION_GPC_EXCHANGE_RATES_CONTAINER_CLASS = `${VALUATIONS_SLUG}-exchange-rates-container`;

const GuidelinePublicCompanies = ({ spreadsheets, onChange, workbook }: GuidelinePublicCompaniesProps) => {
  const gpcSpreadsheet = spreadsheets.gpc;
  const { tableData: initialTableData } = gpcSpreadsheet as { tableData: GPCTableData };
  const performanceMetricsSpreadsheet = spreadsheets.performanceMetrics;
  const exchangeRateSheet = spreadsheets.exchangeRates;
  const { financials, approach, companyName, isDisabled } = initialTableData;
  const { valuations_approach_gpc } = getObjectValue(approach);
  const {
    use_multiple_premium_discount: useMultiplePremiumDiscountFromDB,
    is_evaluating_equity_value: isEvaluatingEquityValue,
    use_forward_multiple_discount: useForwardMultipleDiscountFromDB,
  } = getObjectValue(valuations_approach_gpc);

  const prevEvaluatingRef = useRef(isEvaluatingEquityValue);
  const prevMultiplePremiumDiscountRef = useRef(useMultiplePremiumDiscountFromDB);
  const prevForwardMultipleDiscountRef = useRef(useForwardMultipleDiscountFromDB);
  const {
    resetConfiguration,
    compGroups,
    setAreThereChanges,
    financialsCurrency,
    financialsPeriods,
    setNotesInApproach,
    marketCurrency,
    marketDate,
    measurementDate,
    gpcAttributes,
    publicCompsByDate,
  } = useContext(ValuationContext);

  const { isExportMode } = useLayoutContext();

  const hasDifferentCurrencies = useMemo(
    () => financialsCurrency !== marketCurrency,
    [financialsCurrency, marketCurrency]
  );

  const formatProps = useMemo(
    () => ({
      sourceCurrency: financialsCurrency,
      currencyName: 'Financials Currency',
      ...(hasDifferentCurrencies && {
        additionalCurrencies: [
          {
            currency: marketCurrency,
            currencyType: MARKET_CURRENCY,
            currencyName: 'Market Currency',
          },
        ],
      }),
      marketDate,
    }),
    [marketCurrency, financialsCurrency, marketDate, hasDifferentCurrencies]
  );

  const [format, formatDispatch] = useFormat(formatProps);

  const combinedGPCComparisonPerfMetricAttrs = useCombinedGPCComparisonPerfMetricAttrs(gpcAttributes!) as GPCAttributes;

  const [openGpcDialog, setOpenGpcDialog] = useState(false);
  const [collapsibleColumns, setCollapsibleColumns] = useState({
    [FIRST_NON_FORWARD_ID]: true,
  });
  const [hasMultiplePremiumDiscount, setHasMultiplePremiumDiscount] = useState(
    useMultiplePremiumDiscountFromDB || false
  );
  const approachPanelId = approach.panelId;
  const { notes, setNotes, notesHasChanged, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();

  const { customClasses: performanceMetricsCustomClasses } = usePerformanceMetricsCustomClasses();

  const [cellOptions, handleOnChange] = useCellOptions(onChange, approach.valuations_approach_gpc!);

  const [rowGroups, setRowGroups] = useReducer(rowsGroupsReducer, {
    [SELECTION]: hasMultiplePremiumDiscount ? USER_EXPAND : SYSTEM_COLLAPSE,
  });

  // Store companies as they are removed from the table
  const removedGPCs = useRef<{ capitalIQId: string; gpcComparisonsId: number | undefined }[]>([]);

  const getUpdatedGPC = ({ gpc }: { gpc: PublicComp }) => {
    if (
      (approach.valuations_approach_gpc?.gpc_comparison as GPC[])?.findIndex(
        comparison => comparison.cap_iq_id === gpc?.cap_iq_id?.value
      ) >= 0
    ) {
      return null;
    }

    const flattened = getFlattenedData({
      data: gpc,
      attributes: combinedGPCComparisonPerfMetricAttrs,
    });

    flattened.first_metric_enabled = true;
    flattened.second_metric_enabled = true;
    flattened.first_forward__metric_enabled = true;
    flattened.second_forward_metric_enabled = true;
    flattened.name = flattened.company_name;
    const { calendar_years_financials: calendarYearsFinancials } = parseGpcComparison({
      data: gpc,
      attributes: combinedGPCComparisonPerfMetricAttrs,
    }) as GPC;
    flattened.calendar_years_financials = calendarYearsFinancials;
    flattened.reference_for_backsolve = flattened.cap_iq_id;

    return {
      ...approach,
      valuations_approach_gpc: {
        ...approach.valuations_approach_gpc,
        gpc_comparison: [...(approach.valuations_approach_gpc?.gpc_comparison || []), flattened] as GPC[],
      },
    };
  };

  const [lastSortedColumn, setLastSortedColumn] = useState<{ [key: string]: string } | null>(null);

  useEffect(() => {
    if (prevMultiplePremiumDiscountRef.current !== useMultiplePremiumDiscountFromDB) {
      setHasMultiplePremiumDiscount(useMultiplePremiumDiscountFromDB || false);
      prevMultiplePremiumDiscountRef.current = useMultiplePremiumDiscountFromDB;
    }
  }, [useMultiplePremiumDiscountFromDB]);

  const updateApproach = useCallback(
    (approachGpc, tableData, updatedSortedColumn = {}) => {
      const rowConfigParams = {
        companyName,
        approach: approachGpc,
        allCompGroups: compGroups,
        isDisabled,
        marketDate,
        publicCompsByDate: publicCompsByDate ?? undefined,
      };

      // Update GPC Spreadsheet

      gpcSpreadsheet.reset({
        tableData,
        rowConfig: getGPCRowConfig(rowConfigParams),
        columns: gpcRowTransformer({
          approach: approachGpc,
          financials,
          financialsPeriods: financialsPeriods!,
          financialStatementId: tableData.approach.financial_statement,
          sortedColumn: updatedSortedColumn,
          measurementDate: measurementDate as MeasurementDateName,
        }),
      });

      // Update Performance Metrics Spreadsheet

      const performanceMetricsColumns = performanceMetricsCreateColumns({
        valuationsApproachGpc: approachGpc,
        financials,
      });

      performanceMetricsSpreadsheet.reset({
        columns: performanceMetricsColumns,
        rowConfig: performanceMetricsRowConfig({
          companyName: rowConfigParams.companyName,
          customClasses: performanceMetricsCustomClasses,
          valuationsApproachGpc: rowConfigParams.approach,
        }),
        tableData: {
          ...performanceMetricsSpreadsheet.tableData,
          columns: performanceMetricsColumns,
          customData: cellOptions,
          isDisabled,
        },
      });

      resetConfiguration?.();
    },
    [
      cellOptions,
      companyName,
      compGroups,
      isDisabled,
      gpcSpreadsheet,
      financials,
      financialsPeriods,
      performanceMetricsSpreadsheet,
      measurementDate,
      marketDate,
      performanceMetricsCustomClasses,
      publicCompsByDate,
      resetConfiguration,
    ]
  );

  useEffect(() => {
    const updateTableUIAsync = async () => {
      const rowConfigParams = {
        companyName,
        approach: gpcSpreadsheet.tableData.approach.valuations_approach_gpc as ReverseParsedValuationApproachGPC,
        allCompGroups: compGroups,
        isDisabled,
        marketDate,
        publicCompsByDate: publicCompsByDate ?? undefined,
      };
      const updatedColumns = gpcRowTransformer({
        approach: gpcSpreadsheet.tableData.approach.valuations_approach_gpc!,
        financials,
        financialsPeriods: financialsPeriods!,
        financialStatementId: gpcSpreadsheet.tableData.approach.financial_statement!,
        sortedColumn: {},
        measurementDate: measurementDate as MeasurementDateName,
      });
      gpcSpreadsheet.reset({
        rowConfig: getGPCRowConfig(rowConfigParams),
        columns: updatedColumns,
      });
      await workbook.evaluateSpreadsheets(Object.values(spreadsheets));
    };
    if (prevEvaluatingRef.current !== isEvaluatingEquityValue) {
      updateTableUIAsync();
    }
    if (prevForwardMultipleDiscountRef.current !== useForwardMultipleDiscountFromDB) {
      updateTableUIAsync();
    }
    prevEvaluatingRef.current = isEvaluatingEquityValue;
    prevForwardMultipleDiscountRef.current = useForwardMultipleDiscountFromDB;
  }, [
    isEvaluatingEquityValue,
    useForwardMultipleDiscountFromDB,
    financials,
    financialsPeriods,
    publicCompsByDate,
    measurementDate,
    marketDate,
    gpcSpreadsheet,
    companyName,
    isDisabled,
    compGroups,
    workbook,
    spreadsheets,
  ]);

  const onAdd = async (gpcData: CapIQCompanyData[]) => {
    // To make the implementation of onAdd here and in the comp groups consistent, the argument
    // passed to the function is an array (even when it contains a single object in this case)
    const { gpc } = gpcData[0];
    const updatedApproach = getUpdatedGPC({ gpc: gpc! });
    if (updatedApproach) {
      const { valuations_approach_gpc: approachGpc } = updatedApproach;

      const { tableData } = gpcSpreadsheet;

      tableData.approach.valuations_approach_gpc = {
        ...(approachGpc as ValuationApproachGPC),
        gpc_comparison: approachGpc.gpc_comparison.map((comp: GPC, index: number) => ({
          ...comp,
          order: index,
        })) as GPCExtended[],
      } as ValuationApproachGPCExtended;
      updateApproach(approachGpc, tableData);
    }
  };

  const onRemove = async (deletedComparison: GPCExtended) => {
    const { valuations_approach_gpc: gpc } = approach;
    const compId = deletedComparison.comp_group_id ?? deletedComparison.gpc_approach_comp_group;
    const currentDeletedCompGroups = gpc?.deleted_comp_groups ?? [];

    // 1. update the specific approach to no longer include this thing
    // Exclude the deleted comparison and others removed previously (if any)
    const filteredComparisons = (approach?.valuations_approach_gpc?.gpc_comparison ?? [])
      .filter(
        (comparison: GPCExtended) =>
          comparison.cap_iq_id !== deletedComparison.cap_iq_id &&
          !removedGPCs.current
            .map((item: { capitalIQId: string }) => item.capitalIQId)
            .includes(comparison.cap_iq_id ?? '')
      )
      .map((comp, index) => ({
        ...comp,
        order: index,
      })) as GPCExtended[];

    // Remove the comp group relation of the other companies
    // with the same comp group as the deleted one
    filteredComparisons.forEach(company => {
      if (company.gpc_approach_comp_group === compId) {
        delete company.gpc_approach_comp_group;
      }
      if (company.comp_group_id === compId) {
        delete company.comp_group_id;
      }
    });

    const objectKeyCompGroup = gpc?.valuationapproachgpccompgroup_set?.find(
      item => item.id === deletedComparison.gpc_approach_comp_group
    );

    const valuationKeysToSave = gpc?.valuationapproachgpccompgroup_set?.filter(
      item => item.comp_group !== objectKeyCompGroup?.comp_group
    );

    const tmpValuationsApproachGpc = {
      ...gpc,
      gpc_comparison: filteredComparisons,
      valuationapproachgpccompgroup_set: valuationKeysToSave,
    };

    removedGPCs.current.push({
      // Needed for new approaches that have not been saved yet
      capitalIQId: deletedComparison.cap_iq_id!,
      // Existing approaches (already persisted to the database) have an id
      gpcComparisonsId: deletedComparison.id,
    });

    const { tableData } = gpcSpreadsheet;

    tableData.approach.valuations_approach_gpc = {
      ...(gpc as ValuationApproachGPC),
      valuationapproachgpccompgroup_set: valuationKeysToSave,
      gpc_comparison: filteredComparisons,
      // Add deleted_gpc_comparisons only in existing approaches
      // since they do not make sense in brand new ones
      deleted_gpc_comparisons: approach.id
        ? (removedGPCs.current.map(item => item.gpcComparisonsId).filter(id => id) as number[])
        : [],
      // If the company has a comp group, remove it from the list of deleted comp groups
      deleted_comp_groups: deletedComparison.gpc_approach_comp_group
        ? ([...currentDeletedCompGroups, ...[compId]] as number[])
        : currentDeletedCompGroups,
    };

    updateApproach(tmpValuationsApproachGpc, tableData);
    setAreThereChanges?.(true);
  };

  const getFlattenedItem = useCallback(
    (companyItem: CapIQCompanyData) => {
      const { gpc } = companyItem;
      const { comp_group_id, comp_group_name, ...newGpc } = getObjectValue(gpc);
      const flattened = getFlattenedData({ data: newGpc, attributes: combinedGPCComparisonPerfMetricAttrs });

      flattened.first_metric_enabled = true;
      flattened.second_metric_enabled = true;
      flattened.first_forward_metric_enabled = true;
      flattened.second_forward_metric_enabled = true;
      flattened.reference_for_backsolve = flattened.cap_iq_id;
      flattened.name = flattened.company_name;
      const { calendar_years_financials: calendarYearsFinancials } = parseGpcComparison({
        data: gpc!,
        attributes: combinedGPCComparisonPerfMetricAttrs,
      }) as GPC;
      flattened.calendar_years_financials = calendarYearsFinancials;

      if (comp_group_id && comp_group_name) {
        flattened.comp_group_id = comp_group_id as string;
        flattened.comp_group_name = comp_group_name as string;
        flattened.temp_ref = comp_group_id as string;
      }
      return flattened as unknown as GPCExtended;
    },
    [combinedGPCComparisonPerfMetricAttrs]
  );

  const getCompGroupsData = useCallback(
    (comparisons: GPCExtended[], compGroupToDelete: number) => {
      const { tableData } = gpcSpreadsheet;
      const { valuationapproachgpccompgroup_set } = getObjectValue(tableData.approach.valuations_approach_gpc);
      const compGroupsData =
        valuationapproachgpccompgroup_set?.filter(item => compGroupToDelete !== item.comp_group) || [];
      const newCompGroups = comparisons
        .filter((comparison: GPCExtended) => comparison.comp_group_id && compGroupToDelete !== comparison.comp_group_id)
        .filter((item: GPCExtended) => !compGroupsData.some(compGroupData => compGroupData.id === item.comp_group_id))
        .map((comparisonCompGroupItem: GPCExtended) => ({
          isNew: true,
          comp_group: comparisonCompGroupItem.comp_group_id,
          comp_group_name: comparisonCompGroupItem.comp_group_name,
          use_latest_comp_group_version: true,
        }));
      const uniqueNewCompGroupsData = [...new Map(newCompGroups.map((item: any) => [item.comp_group, item])).values()];
      return [...compGroupsData, ...uniqueNewCompGroupsData];
    },
    [gpcSpreadsheet]
  );

  const getComparisonsData = useCallback(
    (
      comparisons: GPCExtended[],
      comparisonsToDelete: number[],
      compGroupsData: GPCExtended[],
      useLatestVersion: boolean
    ) => {
      const { tableData } = gpcSpreadsheet;
      const { gpc_comparison } = getObjectValue(tableData.approach.valuations_approach_gpc);
      const remainingTransactions = (gpc_comparison ?? []).filter(
        currentTransaction => !comparisonsToDelete.includes(Number(currentTransaction.id ?? currentTransaction.temp_id))
      );
      const comparisonsWithUpdatedCompGroup = remainingTransactions.map(comparison => {
        const transactionCompGroupId = comparison.gpc_approach_comp_group ?? comparison.comp_group_id;
        const hasCompGroup = compGroupsData.some(compGroup => compGroup.id === transactionCompGroupId);
        if (!hasCompGroup) {
          const { gpc_approach_comp_group, comp_group_id, comp_group_name, temp_ref, ...newTransactionObj } =
            comparison;
          return newTransactionObj;
        }

        return comparison;
      });
      comparisons.forEach(comparison => {
        const currentTransactionIdx = comparisonsWithUpdatedCompGroup.findIndex(
          current => current.cap_iq_id === comparison.cap_iq_id
        );
        if (currentTransactionIdx !== -1) {
          const currentTransactionData = comparisonsWithUpdatedCompGroup[currentTransactionIdx] as GPCExtended;
          if (useLatestVersion) {
            comparisonsWithUpdatedCompGroup[currentTransactionIdx] = {
              id: currentTransactionData.id,
              ...comparison,
            };
          } else {
            const { comp_group_id, comp_group_name, temp_ref, gpc_approach_comp_group, ...updatedCurrentTransaction } =
              getObjectValue(currentTransactionData);
            comparisonsWithUpdatedCompGroup[currentTransactionIdx] = {
              id: currentTransactionData.id,
              ...updatedCurrentTransaction,
            };
          }
        } else {
          comparisonsWithUpdatedCompGroup.push(comparison);
        }
      });
      return comparisonsWithUpdatedCompGroup;
    },
    [gpcSpreadsheet]
  );

  const getDeletedCompGroupsIDs = useCallback(
    (compGroupToDelete: number) => {
      const { tableData } = gpcSpreadsheet;
      const { valuationapproachgpccompgroup_set } = getObjectValue(tableData.approach.valuations_approach_gpc);
      const findMatch = (compGroupItem: ValuationApproachGpcCompGroupExtended) =>
        compGroupToDelete === compGroupItem.comp_group;
      return (valuationapproachgpccompgroup_set ?? [])
        .filter(compGroupItem => !compGroupItem.isNew && findMatch(compGroupItem))
        .map(compGroupItem => compGroupItem.id);
    },
    [gpcSpreadsheet]
  );

  const saveCompGroups = useCallback(
    async (
      comparisons: CapIQCompanyData[],
      useLatestVersion: boolean,
      comparisonsToDelete: number[],
      compGroupToDelete: number
    ) => {
      const { tableData } = gpcSpreadsheet;
      const validComparisons = comparisons.filter(item => item.valid_gpc_results);

      const flattenedComparisons = validComparisons.map(companyItem => getFlattenedItem(companyItem));
      const compGroupsData = getCompGroupsData(flattenedComparisons, compGroupToDelete) as GPCExtended[];
      const gpcComparison = getComparisonsData(
        flattenedComparisons,
        comparisonsToDelete,
        compGroupsData,
        useLatestVersion
      );

      const valuationApproachGPC = {
        ...tableData.approach.valuations_approach_gpc,
        gpc_comparison: gpcComparison,
        valuationapproachgpccompgroup_set: compGroupsData,
      };

      if (!isEmpty(comparisonsToDelete)) {
        valuationApproachGPC.deleted_gpc_comparisons = comparisonsToDelete;
      }
      if (compGroupToDelete) {
        const deletedCompGroups = getDeletedCompGroupsIDs(compGroupToDelete);
        valuationApproachGPC.deleted_comp_groups = deletedCompGroups;
      }
      tableData.approach.valuations_approach_gpc = valuationApproachGPC as unknown as ValuationApproachGPCExtended;
      updateApproach(valuationApproachGPC, tableData);
    },
    [getCompGroupsData, getComparisonsData, getDeletedCompGroupsIDs, gpcSpreadsheet, updateApproach, getFlattenedItem]
  );

  const deleteCompGroup = useCallback(
    (compGroupToDelete: number) => {
      const { tableData } = gpcSpreadsheet;

      const { gpc_comparison, valuationapproachgpccompgroup_set, deleted_gpc_comparisons, deleted_comp_groups } =
        getObjectValue(tableData.approach.valuations_approach_gpc);
      const currentDeletedComparisons = deleted_gpc_comparisons || [];
      const currentDeletedCompGroups = deleted_comp_groups || [];

      const comparisonResult = partition(gpc_comparison, item => {
        const compGroupId = item.gpc_approach_comp_group ?? item.comp_group_id;
        return compGroupId === compGroupToDelete;
      });

      const compGroupData = (valuationapproachgpccompgroup_set ?? []).filter(compGroup => {
        const compGroupId = compGroup.id || compGroup.comp_group;
        return compGroupId !== compGroupToDelete;
      });

      const comparisonsToDelete = comparisonResult[0];
      const remainingComparisons = comparisonResult[1];

      const updatedApproach = {
        gpc_comparison: remainingComparisons,
        valuationapproachgpccompgroup_set: compGroupData,
        deleted_comp_groups: [...new Set([...currentDeletedCompGroups, ...[compGroupToDelete]])],
      } as unknown as ValuationApproachGPCExtended;

      if (comparisonsToDelete) {
        updatedApproach.deleted_gpc_comparisons = [...currentDeletedComparisons, ...comparisonsToDelete]
          ?.filter(({ id }: any) => id)
          ?.map(({ id }: any) => id);
      }
      tableData.approach.valuations_approach_gpc = {
        ...tableData.approach.valuations_approach_gpc,
        ...updatedApproach,
      } as unknown as ValuationApproachGPCExtended;
      updateApproach(tableData.approach.valuations_approach_gpc, tableData);
    },
    [gpcSpreadsheet, updateApproach]
  );

  const compGroupContextValue = useMemo(
    () =>
      ({
        saveCompGroups,
        deleteCompGroup,
        tableData: gpcSpreadsheet.tableData,
        approachType: PUBLIC_COMPANIES,
        cellOptions,
        setUseMultiplePremiumDiscount: setHasMultiplePremiumDiscount,
        useMultiplePremiumDiscount: hasMultiplePremiumDiscount,
      }) as unknown as CompGroupContextType,
    [cellOptions, deleteCompGroup, gpcSpreadsheet, saveCompGroups, hasMultiplePremiumDiscount]
  );

  useEffect(() => {
    if (!isEmpty(notes)) {
      setNotesInApproach?.((prevState: any) => {
        const tmpNotes = prevState.filter((note: Note) => note.panelId !== approachPanelId);
        return [...tmpNotes, { notes, panelId: approachPanelId, notesHasChanged }];
      });
    }
  }, [notes, notesHasChanged, approachPanelId, setNotesInApproach]);

  // Multiple Premium Discount
  useEffect(() => {
    setRowGroups({ type: hasMultiplePremiumDiscount ? USER_EXPAND : SYSTEM_COLLAPSE, row: SELECTION });
  }, [hasMultiplePremiumDiscount, setRowGroups]);

  const updateUseMultiplePremiumDiscount = useCallback(async () => {
    const { tableData } = gpcSpreadsheet;
    tableData.approach.valuations_approach_gpc = {
      ...tableData.approach.valuations_approach_gpc,
      use_multiple_premium_discount: hasMultiplePremiumDiscount,
    } as unknown as ValuationApproachGPCExtended;
    updateApproach(tableData.approach.valuations_approach_gpc, tableData);
  }, [gpcSpreadsheet, updateApproach, hasMultiplePremiumDiscount]);

  // Update the Use Multiple Premium Discount when clicking on the button
  useEffect(() => {
    updateUseMultiplePremiumDiscount();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMultiplePremiumDiscount]);

  // End of Multiple Premium Discount

  const sortGPC = useCallback(
    (key, isStringColumn = false, isDateColumn = false) => {
      const { tableData } = gpcSpreadsheet;

      const currentSortedColumn = getStringValue(lastSortedColumn?.[key]);
      const currentSortedColumnIsDesc = currentSortedColumn === SORT_DESC;

      const updatedSortedColumn = {
        [key]: currentSortedColumnIsDesc ? SORT_ASC : SORT_DESC,
      };

      const sortedComparison = (tableData.approach.valuations_approach_gpc?.gpc_comparison ?? []).sort((a, b) => {
        const aValue = a?.[key as keyof GPCExtended];
        const bValue = b?.[key as keyof GPCExtended];
        if (aValue && bValue) {
          setLastSortedColumn?.(updatedSortedColumn);

          if (currentSortedColumnIsDesc) {
            return isStringColumn
              ? getStringValue(aValue).localeCompare(getStringValue(bValue))
              : getNumberValue(aValue) - getNumberValue(bValue);
          }

          return isStringColumn
            ? getStringValue(bValue).localeCompare(getStringValue(aValue))
            : getNumberValue(bValue) - getNumberValue(aValue);
        }

        return 0;
      });

      const valuationApproachGPC = {
        ...tableData.approach.valuations_approach_gpc,
        gpc_comparison: sortedComparison.map((comp, index) => ({
          ...comp,
          order: index,
        })),
      };

      tableData.approach.valuations_approach_gpc = valuationApproachGPC as unknown as ValuationApproachGPCExtended;
      updateApproach(valuationApproachGPC, tableData, updatedSortedColumn);
    },
    [gpcSpreadsheet, lastSortedColumn, updateApproach]
  );

  return (
    <CustomContainer>
      {/* Public comps */}
      <CustomCompactContainer>
        {/* Public comps */}
        <Grid className={VALUATION_GPC_PUBLIC_COMPANIES_CONTAINER_CLASS} size={{ xs: 12 }} data-ref={PdfExportTargets.PUBLIC_COMPS.id} style={isExportMode ? EXPORT_MODE_STYLES : {}}>
          <CompGroupContext.Provider value={compGroupContextValue}>
            <ScalarSpreadsheet
              {...gpcSpreadsheet}
              accessSheet
              addSingleRow={() => setOpenGpcDialog(true)}
              rowGroups={rowGroups}
              setRowGroups={setRowGroups}
              allowSortColumn
              collapsibleColumns={collapsibleColumns}
              deleteRowFn={onRemove}
              format={format}
              formatDispatch={formatDispatch}
              key={gpcSpreadsheet.name}
              // @ts-expect-error: ScalarSpreadsheet is not typed
              onChange={handleOnChange}
              setCollapsibleColumns={setCollapsibleColumns}
              sheet={gpcSpreadsheet}
              // @ts-expect-error: ScalarSpreadsheet is not typed
              sortColumnFn={sortGPC}
              // @ts-expect-error: ScalarSpreadsheet is not typed
              sortedColumn={lastSortedColumn}
              tableTerms={{
                tableSlug: VALUATION_GPC_TABLE_SLUG,
              }}
              workbook={workbook}
            />
          </CompGroupContext.Provider>
        </Grid>

        {/* Add comparable company */}
        <Grid size={{ xs: 12 }}>
          <AddGpcDialog
            currentComps={
              gpcSpreadsheet.tableData.approach.valuations_approach_gpc?.gpc_comparison as unknown as GPCExtended[]
            }
            deleteCompGroup={deleteCompGroup}
            disabled={isDisabled}
            onAdd={onAdd}
            openGpcDialog={openGpcDialog}
            saveCompGroups={saveCompGroups}
            setOpenGpcDialog={setOpenGpcDialog}
            source="valuation"
            tableData={initialTableData}
          />
        </Grid>
      </CustomCompactContainer>

      {/* Performance metrics */}
      <Grid className={VALUATION_GPC_PERFORMANCE_METRICS_CONTAINER_CLASS} size={{ xs: 12 }}>
        <ScalarSpreadsheet
          {...performanceMetricsSpreadsheet}
          key={performanceMetricsSpreadsheet.name}
          onChange={onChange}
          sheet={performanceMetricsSpreadsheet}
          workbook={workbook}
          format={format}
          formatDispatch={formatDispatch}
          displayColumnIndicator
          displayRowIndicator
        />
      </Grid>

      {/* Exchange rates */}
      {hasDifferentCurrencies && (
        <Grid className={VALUATION_GPC_EXCHANGE_RATES_CONTAINER_CLASS} size={{ xs: 12 }}>
          <ExchangeRatesReferenceTable
            exchangeRateSheet={exchangeRateSheet}
            onChange={onChange}
            format={format}
            formatDispatch={formatDispatch}
          />
        </Grid>
      )}

      <WidgetDrawer
        applicationContext={WidgetDrawerApplicationContext.GPC}
        notesProps={{
          pageType: VALUATIONS_PAGE_VALUE,
          pageTypeKey: VALUATIONS_PAGE_KEY,
          pageTypeId: approach.id,
          notes,
          isApproach: true,
          setNotes,
          onAddNote,
          onUpdateNotes,
          onDeleteNote,
          isDisabled,
        }}
      />
    </CustomContainer>
  );
};

export default GuidelinePublicCompanies;
